import React, { forwardRef, useCallback } from "react";
import { useField } from "formik";
import MaskInput from "react-input-mask";
import { Input as MuiInput, Icon } from "@material-ui/core";

// Helper functions for sizing - same as in Input component
const getInputSizeStyle = (size) => {
  switch (size) {
    case "small":
      return { width: "calc(100% / 3 - 6px)" };
    case "medium":
      return { width: "calc(100% / 2 - 12px)" };
    case "large":
    default:
      return { width: "100%" };
  }
};

// Helper functions for wrapper variants
const getNakedWrapperStyle = () => ({
  padding: 0,
  marginBottom: 0,
});

const getDefaultWrapperStyle = (background, color) => ({
  backgroundColor: background ? background : "rgba(0, 0, 0, 0.04)",
  padding: "8px 15px 0px",
  marginBottom: "10px",
  borderBottom: `1px solid ${color}`,
  borderRadius: "4px 4px 0 0",
  boxSizing: "border-box",
});

const MaskedInput = forwardRef(function(
  {
    size = "large",
    label,
    icon,
    iconPosition = "start",
    tip,
    multiline,
    inputClassName,
    rows,
    background,
    color = "#255915",
    variant,
    name,
    disabled,
    inputWrapperStyle = {},
    ...other
  },
  ref
) {
  // Combine all styles
  const baseWrapperStyle =
    variant === "naked"
      ? getNakedWrapperStyle()
      : getDefaultWrapperStyle(background, color);

  const sizeStyle = getInputSizeStyle(size);

  const finalWrapperStyle = {
    ...baseWrapperStyle,
    ...sizeStyle,
    ...inputWrapperStyle,
  };

  // Styles for the elements
  const inputGroupStyle = {
    display: "flex",
  };

  const inputStyle = {
    display: "block",
    width: "100%",
  };

  const inputLabelStyle = {
    fontSize: "12px",
    color: color,
    letterSpacing: "0.4px",
  };

  const iconStyle = {
    marginLeft: "5px",
    marginRight: "5px",
    order: iconPosition === "start" ? 1 : 2,
  };

  return (
    <>
      <div style={finalWrapperStyle}>
        {label && <div style={inputLabelStyle}>{label}</div>}
        <div style={inputGroupStyle}>
          {icon && (
            <div style={iconStyle}>
              <Icon>{icon}</Icon>
            </div>
          )}
          <MaskInput {...other} disabled={disabled}>
            {() => (
              <MuiInput
                disableUnderline
                inputRef={ref}
                multiline={multiline}
                rows={rows}
                name={name}
                style={inputStyle}
                data-testid="input"
              />
            )}
          </MaskInput>
        </div>
      </div>
      {tip && <div>{tip}</div>}
    </>
  );
});

export function MappedMaskedInput(props) {
  const errorStyle = { color: "red" };
  const formGroupStyle = { marginBottom: "1.75rem" };

  function getTip(meta = {}, tip) {
    if (meta.touched && meta.error && typeof meta.error !== "object") {
      return <span style={errorStyle}>{meta.error}</span>;
    }
    if (tip) {
      return <span>{tip}</span>;
    }
  }

  const [field, meta] = useField(props.name);

  const getInputClasses = useCallback(() => {
    if (meta.touched && meta.error) {
      return "is-invalid";
    }

    if (meta.touched && !meta.error) {
      return "is-valid";
    }

    return "";
  }, [meta]);

  return (
    <div style={formGroupStyle}>
      <MaskedInput
        {...field}
        {...props}
        inputClassName={getInputClasses()}
        tip={getTip(meta, props.tip)}
      />
    </div>
  );
}

export default MaskedInput;
